

import React, { useState, useEffect } from "react";
import axios from "axios";

const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET;
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_NAME;
const API_URL = process.env.REACT_APP_API_URL;

export default function JobPostForm() {
  const [tab, setTab] = useState("structured");
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("token");

  const [structured, setStructured] = useState({
    title: "",
    companyName: "",
    salary: "",
    requirements: "",
    address: "",
    contactNumber: "",
    email: "",
  });

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [cloudUrl, setCloudUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [writeUp, setWriteUp] = useState("");

  // ────────────────────────────────────────────────
  // NEW: Job listing, edit, delete, pagination
  // ────────────────────────────────────────────────
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(10);
  const [editingJob, setEditingJob] = useState(null); // null = not editing

  // Fetch all jobs on mount and after post/delete
  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      const res = await axios.get(`${API_URL}admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(res.data)
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to load jobs:", err);
      alert("Could not load jobs");
    } finally {
      setLoadingJobs(false);
    }
  };

  // Delete job
  const handleDelete = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;

    try {
      await axios.delete(`${API_URL}admin/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Job deleted successfully");
      fetchJobs(); // refresh list
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to delete job");
    }
  };

  // Start editing a job
  const handleEditStart = (job) => {
    setEditingJob(job);
    // You can switch tab or scroll to form if desired
    if (job.type === "image") {
      setTab("image");
      setWriteUp(job.writeUp || "");
      setCloudUrl(job.imageUrl || "");
      setPreview(job.imageUrl || "");
      setFile(null);
    } else {
      setTab("structured");
      setStructured({
        title: job.title || "",
        companyName: job.companyName || "",
        salary: job.salary || "",
        requirements: job.requirements || "",
        address: job.address || "",
        contactNumber: job.contactNumber || "",
        email: job.email || "",
      });
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingJob(null);
    // Reset form to empty
    setStructured({
      title: "",
      companyName: "",
      salary: "",
      requirements: "",
      address: "",
      contactNumber: "",
      email: "",
    });
    setFile(null);
    setPreview("");
    setCloudUrl("");
    setWriteUp("");
  };

  // ALL FIELDS ARE NOW OPTIONAL — NO VALIDATION
  const canSubmit = true;
  const disableBtn = submitting || uploading;

  const onFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setCloudUrl("");
    if (f) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview("");
    }
  };

  const uploadImage = async () => {
    if (!file || cloudUrl) return;

    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_preset", UPLOAD_PRESET);

    try {
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        fd,
        { timeout: 30000 }
      );
      setCloudUrl(res.data.secure_url);
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.error?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (tab === "image" && file && !cloudUrl) {
      await uploadImage();
      if (!cloudUrl) return;
    }

    setSubmitting(true);

    try {
      let endpoint = `${API_URL}admin/postjob/`;
      let method = "post";
      let payload = {};

      if (editingJob) {
        // Update existing job
        endpoint = `${API_URL}admin/${editingJob._id}`;
        method = "put";
      }

      if (tab === "image") {
        endpoint += editingJob ? "" : "image";
        payload = { imageUrl: cloudUrl, writeUp };
      } else {
        endpoint += editingJob ? "" : "structured";
        payload = { ...structured };
      }

      await axios({
        method,
        url: endpoint,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 15000,
      });

      alert(editingJob ? "Job updated successfully!" : "Job posted successfully!");

      // Reset form
      if (tab === "structured") {
        setStructured({
          title: "",
          companyName: "",
          salary: "",
          requirements: "",
          address: "",
          contactNumber: "",
          email: "",
        });
      } else {
        setFile(null);
        setPreview("");
        setCloudUrl("");
        setWriteUp("");
      }

      setEditingJob(null);
      fetchJobs(); // refresh list
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || `Failed to ${editingJob ? "update" : "post"} job`);
    } finally {
      setSubmitting(false);
    }
  };

  // Pagination logic
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = jobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(jobs.length / jobsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* ── Post/Edit Form ──────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-10">
        <h2 className="text-2xl font-bold text-center text-indigo-700 mb-6">
          {editingJob ? "Edit Job" : "Post a New Job"}
        </h2>

        {editingJob && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
            You are editing job ID: <strong>{editingJob._id}</strong>
            <button
              onClick={handleCancelEdit}
              className="ml-4 text-red-600 hover:underline"
            >
              Cancel Edit
            </button>
          </div>
        )}

        <div className="flex justify-center gap-6 mb-8">
          {["structured", "image"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-lg font-medium transition-all ${
                tab === t
                  ? "bg-indigo-600 text-white shadow-md"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {t === "image" ? "Image + Write-up" : "Structured Form"}
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* ... (your existing form fields for image & structured remain unchanged) ... */}

          {tab === "image" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                {preview && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600">
                      {cloudUrl ? "Uploaded" : "Preview"}
                    </p>
                    <img
                      src={cloudUrl || preview}
                      alt="preview"
                      className="mt-1 max-h-48 w-full object-contain rounded border"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Write-up
                </label>
                <textarea
                  placeholder="Describe the job, requirements, benefits..."
                  value={writeUp}
                  onChange={(e) => setWriteUp(e.target.value)}
                  rows={5}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </>
          )}

          {tab === "structured" && (
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(structured).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {key.replace(/([A-Z])/g, " $1").trim()}
                  </label>
                  <input
                    type={key === "email" ? "email" : "text"}
                    value={value}
                    onChange={(e) =>
                      setStructured({ ...structured, [key]: e.target.value })
                    }
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              ))}
            </div>
          )}

          <button
            type="submit"
            disabled={disableBtn}
            className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center transition-all ${
              disableBtn
                ? "bg-gray-400 cursor-not-allowed text-white"
                : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
            }`}
          >
            {uploading
              ? "Uploading Image..."
              : submitting
              ? editingJob
                ? "Updating Job..."
                : "Posting Job..."
              : tab === "image" && file && !cloudUrl
              ? "Upload & Post"
              : editingJob
              ? "Update Job"
              : "Post Job"}
          </button>
        </form>
      </div>

      {/* ── Job List Section ──────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-indigo-700 mb-6">
          Posted Jobs
        </h2>

        {loadingJobs ? (
          <p className="text-center text-gray-500">Loading jobs...</p>
        ) : jobs.length === 0 ? (
          <p className="text-center text-gray-500">No jobs posted yet.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title / Write-up
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentJobs.map((job) => (
                    <tr key={job._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {job.type || (job.imageUrl ? "Image" : "Structured")}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {job.title || job.writeUp?.substring(0, 80) + (job.writeUp?.length > 80 ? "..." : "") || "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(job.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditStart(job)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(job._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex justify-center gap-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}